import { useCallback, useEffect, useMemo, useState } from "react";
import { publicApi } from "@/api";
import { toast } from "sonner";
import {
    Activity,
    Check,
    Copy,
    RefreshCw,
    Send,
    Mail as MailIcon,
    Inbox,
    ShieldCheck,
    ShieldAlert,
    Radio,
    CircleOff,
} from "lucide-react";
import DeliveryRow from "@/components/DeliveryRow";

function CopyButton({ value, testid }) {
    const [copied, setCopied] = useState(false);
    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            toast.success("Copied to clipboard");
            setTimeout(() => setCopied(false), 1400);
        } catch {
            toast.error("Clipboard unavailable");
        }
    };
    return (
        <button
            onClick={onCopy}
            className="ghost-btn"
            data-testid={testid}
            title="Copy"
        >
            {copied ? <Check size={13} /> : <Copy size={13} />}
            {copied ? "copied" : "copy"}
        </button>
    );
}

function StatusHeader({ config }) {
    const hmacEnforced = !!config?.hmac_enforced;
    const outboundLive = config?.outbound_mode === "live";
    const imapOn = !!config?.imap_configured;
    const smtpOn = !!config?.smtp_configured;
    return (
        <div
            className="flex flex-wrap items-center gap-2"
            data-testid="status-header"
        >
            <span
                className={`badge ${hmacEnforced ? "ok" : "warn"}`}
                data-testid="badge-hmac"
            >
                {hmacEnforced ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
                HMAC: {hmacEnforced ? "ENFORCED" : "DISABLED (dev)"}
            </span>
            <span
                className={`badge ${imapOn ? "ok" : "warn"}`}
                data-testid="badge-imap"
            >
                {imapOn ? <Inbox size={12} /> : <CircleOff size={12} />}
                IMAP: {imapOn ? "POLLING" : "OFF"}
            </span>
            <span
                className={`badge ${smtpOn ? "ok" : "warn"}`}
                data-testid="badge-smtp"
            >
                {smtpOn ? <MailIcon size={12} /> : <CircleOff size={12} />}
                SMTP: {smtpOn ? "READY" : "DRY-RUN"}
            </span>
            <span
                className={`badge ${outboundLive ? "ok" : "warn"}`}
                data-testid="badge-outbound"
            >
                {outboundLive ? <Radio size={12} /> : <CircleOff size={12} />}
                Glip env-fallback: {outboundLive ? "ON" : "OFF"}
            </span>
        </div>
    );
}

export default function Dashboard() {
    const [config, setConfig] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sendingWebhook, setSendingWebhook] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const ingressUrl = useMemo(() => {
        if (!config) return "";
        const base =
            (config.public_base_url && config.public_base_url.replace(/\/$/, "")) ||
            process.env.REACT_APP_BACKEND_URL;
        return `${base}${config.ingress_path}`;
    }, [config]);

    const loadConfig = useCallback(async () => {
        try {
            const res = await publicApi.get("/config");
            setConfig(res.data);
        } catch {
            toast.error("Failed to load config");
        }
    }, []);

    const loadDeliveries = useCallback(async () => {
        setLoading(true);
        try {
            const res = await publicApi.get("/deliveries");
            setDeliveries(res.data.deliveries || []);
        } catch {
            toast.error("Failed to load deliveries");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadConfig();
        loadDeliveries();
    }, [loadConfig, loadDeliveries]);

    const sendWebhookSample = async () => {
        setSendingWebhook(true);
        try {
            const res = await publicApi.post("/deliveries/test-sample");
            const count = (res.data.deliveries || []).length;
            toast.success(`Webhook sample sent · ${count} delivery/ies`);
            if (res.data.deliveries?.[0]?.id)
                setExpandedId(res.data.deliveries[0].id);
            await loadDeliveries();
        } catch {
            toast.error("Webhook sample failed");
        } finally {
            setSendingWebhook(false);
        }
    };

    const sendEmailSample = async () => {
        setSendingEmail(true);
        try {
            const res = await publicApi.post("/deliveries/test-email-sample");
            const count = (res.data.deliveries || []).length;
            toast.success(`Email sample parsed · ${count} delivery/ies`);
            if (res.data.deliveries?.[0]?.id)
                setExpandedId(res.data.deliveries[0].id);
            await loadDeliveries();
        } catch {
            toast.error("Email sample failed");
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <div className="fade-in">
            <header className="flex items-start justify-between gap-4 mb-8 flex-wrap">
                <div>
                    <div
                        className="mono text-xs uppercase tracking-[0.25em]"
                        style={{ color: "var(--text-faint)" }}
                    >
                        samsara · middleware · glip + email
                    </div>
                    <h1
                        className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2 term-glow"
                        style={{ color: "var(--text)" }}
                        data-testid="page-title"
                    >
                        <span style={{ color: "var(--accent)" }}>&gt;</span>{" "}
                        dashboard
                    </h1>
                    <p
                        className="mt-3 text-sm max-w-2xl"
                        style={{ color: "var(--text-dim)" }}
                    >
                        Two ingress paths, two egress types, one rule engine.
                        Inspect every delivery side-by-side.
                    </p>
                </div>
                <StatusHeader config={config} />
            </header>

            {/* Ingress info — both paths */}
            <section
                className="panel p-6 mb-8"
                data-testid="ingress-panel"
            >
                <div className="grid md:grid-cols-2 gap-4">
                    {/* Webhook ingress */}
                    <div className="panel-inset p-4">
                        <div
                            className="mono text-[10px] uppercase tracking-[0.2em] mb-2"
                            style={{ color: "var(--text-faint)" }}
                        >
                            ingress · samsara webhook
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span
                                className="mono text-xs"
                                style={{ color: "var(--accent)" }}
                            >
                                POST
                            </span>
                            <code
                                className="mono text-xs break-all flex-1"
                                style={{ color: "var(--text)" }}
                                data-testid="ingress-url"
                            >
                                {ingressUrl || "loading…"}
                            </code>
                            {ingressUrl && (
                                <CopyButton
                                    value={ingressUrl}
                                    testid="copy-ingress-btn"
                                />
                            )}
                        </div>
                        <button
                            onClick={sendWebhookSample}
                            disabled={sendingWebhook}
                            className="ghost-btn mt-3"
                            data-testid="send-sample-btn"
                        >
                            {sendingWebhook ? (
                                <RefreshCw size={11} className="animate-spin" />
                            ) : (
                                <Send size={11} />
                            )}
                            send webhook sample (HarshEvent)
                        </button>
                    </div>
                    {/* Email ingress */}
                    <div className="panel-inset p-4">
                        <div
                            className="mono text-[10px] uppercase tracking-[0.2em] mb-2"
                            style={{ color: "var(--text-faint)" }}
                        >
                            ingress · email mailbox (IMAP)
                        </div>
                        <div
                            className="mono text-xs"
                            style={{
                                color: config?.imap_configured
                                    ? "var(--text)"
                                    : "var(--text-faint)",
                            }}
                            data-testid="imap-status"
                        >
                            {config?.imap_configured
                                ? `Polling ${config.imap_host_masked}`
                                : "Not configured · set IMAP_* env vars + restart"}
                        </div>
                        <div
                            className="mono text-[11px] mt-1"
                            style={{ color: "var(--text-dim)" }}
                        >
                            Sender filter:{" "}
                            <span className="kbd">
                                {config?.imap_sender_filter || "—"}
                            </span>
                        </div>
                        <button
                            onClick={sendEmailSample}
                            disabled={sendingEmail}
                            className="ghost-btn mt-3"
                            data-testid="send-email-sample-btn"
                        >
                            {sendingEmail ? (
                                <RefreshCw size={11} className="animate-spin" />
                            ) : (
                                <MailIcon size={11} />
                            )}
                            send test email from sample
                        </button>
                    </div>
                </div>
            </section>

            {/* Deliveries */}
            <section
                className="panel p-6"
                data-testid="deliveries-panel"
            >
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <Activity
                            size={16}
                            style={{ color: "var(--accent)" }}
                        />
                        <h2
                            className="mono text-sm uppercase tracking-[0.18em]"
                            style={{ color: "var(--text)" }}
                        >
                            deliveries
                        </h2>
                        <span
                            className="mono text-xs"
                            style={{ color: "var(--text-faint)" }}
                            data-testid="deliveries-count"
                        >
                            last {deliveries.length}
                        </span>
                    </div>
                    <button
                        onClick={loadDeliveries}
                        className="ghost-btn"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw
                            size={12}
                            className={loading ? "animate-spin" : ""}
                        />
                        refresh
                    </button>
                </div>

                {deliveries.length === 0 && !loading && (
                    <div
                        className="panel-inset p-10 text-center"
                        data-testid="empty-state"
                    >
                        <div
                            className="mono text-xs uppercase tracking-widest mb-2"
                            style={{ color: "var(--text-faint)" }}
                        >
                            no deliveries yet
                        </div>
                    </div>
                )}

                <div className="flex flex-col gap-2">
                    {deliveries.map((d) => (
                        <DeliveryRow
                            key={d.id}
                            delivery={d}
                            expanded={expandedId === d.id}
                            onToggle={() =>
                                setExpandedId(
                                    expandedId === d.id ? null : d.id,
                                )
                            }
                        />
                    ))}
                </div>
            </section>
        </div>
    );
}
